import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { Tab, Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { toast } from 'react-toastify';
import ManualQuote from './ManualQuote';

function Part() {
  const { user } = useAuth();
  const { part_number } = useParams();
  const navigate = useNavigate();
  const [part, setPart] = useState(null);
  const [partFiles, setPartFiles] = useState([]);
  const [productionLogs, setProductionLogs] = useState([]);
  const [shops, setShops] = useState([]);
  const [machines, setMachines] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState({ section: null, enabled: false });
  const [formData, setFormData] = useState({});
  const [fileToUpload, setFileToUpload] = useState(null);
  const [fileType, setFileType] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'default' });

  useEffect(() => {
    const fetchPartDetails = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const { data: partData, error: partError } = await supabase
          .from('parts')
          .select(`
            part_number,
            part_number_master,
            description,
            revision,
            material,
            shop_id,
            machine_id,
            last_run_date,
            average_run_time,
            setup_notes,
            stock_usage,
            minimum_stock_level,
            kanban_stock,
            created_at,
            updated_at,
            status,
            unit_price,
            supplier_id,
            production_notes,
            batch_size,
            inventory_quantity,
            shops(id, name),
            machines(id, name),
            suppliers(id, name)
          `)
          .eq('part_number', part_number)
          .single();
        if (partError) throw partError;
        setPart(partData);
        setFormData(partData);

        const { data: files, error: filesError } = await supabase
          .from('part_files')
          .select('id, part_number, file_name, file_path, file_type, uploaded_at')
          .eq('part_number', part_number);
        if (filesError) throw filesError;
        setPartFiles(files || []);

        const { data: logs, error: logsError } = await supabase
          .from('production_logs')
          .select('log_id, job_id, part_number, quantity_produced, production_date, shop_id, machine_id, shops(id, name), machines(id, name)')
          .eq('part_number', part_number);
        if (logsError) throw logsError;
        setProductionLogs(logs || []);

        const { data: quotesData, error: quotesError } = await supabase
          .from('quotes')
          .select('*')
          .eq('part_number', part_number);
        if (quotesError) throw quotesError;
        setQuotes(quotesData || []);

        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw shopsError;
        setShops(shopsData || []);

        const { data: machinesData, error: machinesError } = await supabase
          .from('machines')
          .select('id, name');
        if (machinesError) throw machinesError;
        setMachines(machinesData || []);

        const { data: suppliersData, error: suppliersError } = await supabase
          .from('suppliers')
          .select('id, name');
        if (suppliersError) throw suppliersError;
        setSuppliers(suppliersData || []);
      } catch (err) {
        console.error('Error fetching part details:', err);
        toast.error('Failed to fetch part details.');
      } finally {
        setLoading(false);
      }
    };

    fetchPartDetails();
  }, [user, part_number]);

  useEffect(() => {
    document.title = `${part ? part.part_number : 'Loading...'}`;
  }, [part]);

  const handleEditToggle = (section) => {
    if (editMode.section === section && editMode.enabled) {
      setEditMode({ section: null, enabled: false });
    } else {
      setEditMode({ section, enabled: true });
      setFormData({ ...part });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async (section) => {
    try {
      const updatedData = {};
      if (section === 'general') {
        updatedData.part_number_master = formData.part_number_master || null;
        updatedData.description = formData.description || null;
        updatedData.revision = formData.revision || null;
        updatedData.material = formData.material || null;
        updatedData.part_number = `${formData.part_number_master || ''}-${formData.revision || '0'}`;
      } else if (section === 'production') {
        updatedData.last_run_date = formData.last_run_date || null;
        updatedData.average_run_time = formData.average_run_time ? parseFloat(formData.average_run_time) : null;
        updatedData.setup_notes = formData.setup_notes || null;
        updatedData.batch_size = formData.batch_size ? parseInt(formData.batch_size) : null;
      } else if (section === 'inventory') {
        updatedData.unit_price = formData.unit_price ? parseFloat(formData.unit_price) : null;

        if (formData.inventory_adjustment) {
          const adjustmentValue = parseInt(formData.inventory_adjustment);
          if (!isNaN(adjustmentValue)) {
            const currentQuantity = part.inventory_quantity || 0;
            updatedData.inventory_quantity = currentQuantity + adjustmentValue;
            if (updatedData.inventory_quantity < 0) {
              updatedData.inventory_quantity = 0;
              toast.warning('Inventory cannot go below zero. Setting to zero.');
            }
          }
        }
      } else if (section === 'meta') {
        updatedData.shop_id = formData.shop_id ? parseInt(formData.shop_id) : null;
        updatedData.machine_id = formData.machine_id ? parseInt(formData.machine_id) : null;
        updatedData.supplier_id = formData.supplier_id ? parseInt(formData.supplier_id) : null;
        updatedData.production_notes = formData.production_notes || null;
        updatedData.status = formData.status || 'Active';
      }

      const { error } = await supabase
        .from('parts')
        .update(updatedData)
        .eq('part_number', part_number);
      if (error) throw error;

      const updatedPart = {
        ...part,
        ...updatedData,
        shops: part.shops,
        machines: part.machines,
        suppliers: part.suppliers,
      };
      setPart(updatedPart);

      if (section === 'general') {
        const newPartNumber = `${formData.part_number_master || ''}-${formData.revision || '0'}`;
        if (newPartNumber !== part_number) {
          navigate(`/part/${newPartNumber}`);
        }
      }

      window.location.reload();

      setEditMode({ section: null, enabled: false });
      toast.success('Part details updated successfully!');
    } catch (err) {
      console.error('Error updating part:', err);
      toast.error('Failed to update part details.');
    }
  };

  const handleFileUpload = async () => {
    if (!fileToUpload || !fileType) {
      toast.warning('Please select a file and file type.');
      return;
    }

    try {
      const filePath = `part-files/${part_number}/${fileToUpload.name}`;
      const { error: uploadError } = await supabase.storage
        .from('part-files')
        .upload(filePath, fileToUpload);
      if (uploadError) throw uploadError;

      const { data, error: insertError } = await supabase
        .from('part_files')
        .insert({
          part_number,
          file_name: fileToUpload.name,
          file_path: filePath,
          file_type: fileType,
          uploaded_by: user.id,
        })
        .select();
      if (insertError) throw insertError;

      setPartFiles([...partFiles, data[0]]);
      setFileToUpload(null);
      setFileType('');
      setIsUploadModalOpen(false);
      toast.success('File uploaded successfully!');
    } catch (err) {
      console.error('Error uploading file:', err);
      toast.error('Failed to upload file.');
    }
  };

  const handleFileDownload = async (file_path) => {
    try {
      const { data, error } = await supabase.storage
        .from('part-files')
        .download(file_path);
      if (error) throw error;

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = file_path.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('File downloaded successfully!');
    } catch (err) {
      console.error('Error downloading file:', err);
      toast.error('Failed to download file.');
    }
  };

  const handleFilePreview = async (file_path) => {
    try {
      const { data, error } = await supabase.storage
        .from('part-files')
        .createSignedUrl(file_path, 60);
      if (error) throw error;

      setPreviewFile(data.signedUrl);
      setIsPreviewModalOpen(true);
    } catch (err) {
      console.error('Error generating preview URL:', err);
      toast.error('Failed to preview file.');
    }
  };

  const handleFileDelete = (file_id, file_path) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete File',
      message: 'Are you sure you want to delete this file?',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const { error: deleteError } = await supabase.storage
            .from('part-files')
            .remove([file_path]);
          if (deleteError) throw deleteError;

          const { error: dbError } = await supabase
            .from('part_files')
            .delete()
            .eq('id', file_id);
          if (dbError) throw dbError;

          setPartFiles(partFiles.filter(file => file.id !== file_id));
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'default' });
          toast.success('File deleted successfully!');
        } catch (err) {
          console.error('Error deleting file:', err);
          toast.error('Failed to delete file.');
        }
      }
    });
  };

  const handleQuoteSave = async (quoteData) => {
    try {
      const { error } = await supabase
        .from('quotes')
        .insert({
          part_number,
          part_number_display: quoteData.partNumber,
          revision: quoteData.revision,
          description: quoteData.description,
          quantity: parseInt(quoteData.quantity),
          price_each: parseFloat(quoteData.priceEach),
          quote_number: quoteData.quoteNumber,
          date: quoteData.date,
          good_until: quoteData.goodUntil,
          pricing_ranges: quoteData.pricingRanges,
          customer_data: quoteData.customerData,
          created_by: user.id,
        });
      if (error) throw error;

      const { data: newQuotes } = await supabase
        .from('quotes')
        .select('*')
        .eq('part_number', part_number);
      setQuotes(newQuotes || []);
      toast.success('Quote saved successfully!');
    } catch (err) {
      console.error('Error saving quote:', err);
      toast.error('Failed to save quote.');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-emerald-100 text-emerald-700';
      case 'Obsolete':
        return 'bg-red-100 text-red-700';
      case 'In Development':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  const handleDeletePart = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Part',
      message: 'Are you sure you want to delete this part? This will permanently delete all associated files, production logs, and quotes. This action cannot be undone.',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          const { data: associatedJobs, error: jobsError } = await supabase
            .from('jobs')
            .select('id, po_number')
            .eq('part_number', part_number);
          if (jobsError) throw new Error(`Failed to check associated jobs: ${jobsError.message}`);

          if (associatedJobs && associatedJobs.length > 0) {
            setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'default' });
            toast.error(`Cannot delete part. It is associated with ${associatedJobs.length} job(s). Remove the part from these jobs first.`);
            return;
          }

          if (partFiles.length > 0) {
            const filePaths = partFiles.map(file => file.file_path);
            const { error: storageError } = await supabase.storage
              .from('part-files')
              .remove(filePaths);
            if (storageError) throw new Error(`Failed to delete part files from storage: ${storageError.message}`);

            const { error: filesError } = await supabase
              .from('part_files')
              .delete()
              .eq('part_number', part_number);
            if (filesError) throw new Error(`Failed to delete part files from database: ${filesError.message}`);
          }

          const { error: logsError } = await supabase
            .from('production_logs')
            .delete()
            .eq('part_number', part_number);
          if (logsError) throw new Error(`Failed to delete production logs: ${logsError.message}`);

          const { error: quotesError } = await supabase
            .from('quotes')
            .delete()
            .eq('part_number', part_number);
          if (quotesError) throw new Error(`Failed to delete quotes: ${quotesError.message}`);

          const { error: partError } = await supabase
            .from('parts')
            .delete()
            .eq('part_number', part_number);
          if (partError) throw new Error(`Failed to delete part: ${partError.message}`);

          toast.success('Part deleted successfully!');
          navigate('/parts');
        } catch (err) {
          console.error('Error deleting part:', err);
          toast.error(err.message);
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  if (!part) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <p className="text-red-500">Part not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="p-4">
        {/* Header with Breadcrumb */}
        <div className="bg-white rounded-sm border border-slate-200 mb-4">
          <div className="px-4 py-3 flex justify-between items-center border-b border-slate-100">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/parts" className="text-slate-500 hover:text-slate-700 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Parts
              </Link>
              <span className="text-slate-300">/</span>
              <span className="font-semibold text-slate-800">{part.part_number}</span>
              <span className={`ml-2 px-2 py-0.5 rounded-sm text-xs font-medium ${getStatusBadge(part.status)}`}>
                {part.status || 'Active'}
              </span>
            </div>
            <button
              onClick={handleDeletePart}
              className="px-2.5 py-1.5 text-xs text-red-500 border border-red-200 rounded-sm hover:bg-red-50 hover:border-red-300 transition-colors"
            >
              Delete Part
            </button>
          </div>
          <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-slate-500">Description</p>
              <p className="text-sm font-medium text-slate-800 truncate" title={part.description}>{part.description || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Material</p>
              <p className="text-sm font-medium text-slate-800">{part.material || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Inventory</p>
              <p className="text-sm font-medium text-slate-800 tabular-nums">{part.inventory_quantity || 0}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Unit Price</p>
              <p className="text-sm font-medium text-slate-800 tabular-nums">${part.unit_price || '0.00'}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tab.Group>
          <Tab.List className="flex gap-1 bg-white rounded-sm border border-slate-200 p-1 mb-4">
            {['Overview', 'Production Logs', 'Files', 'Quote'].map((tab) => (
              <Tab
                key={tab}
                className={({ selected }) =>
                  `px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                    selected
                      ? 'bg-slate-700 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`
                }
              >
                {tab}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels>
            {/* Overview Panel */}
            <Tab.Panel className="space-y-4">
              {/* General Information */}
              <div className="bg-white rounded-sm border border-slate-200">
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">General Information</h2>
                  <button
                    onClick={() => handleEditToggle('general')}
                    className="px-2.5 py-1.5 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                  >
                    {editMode.section === 'general' && editMode.enabled ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                <div className="p-4">
                  {editMode.section === 'general' && editMode.enabled ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Part Number Master</label>
                        <input
                          type="text"
                          value={formData.part_number_master || ''}
                          onChange={(e) => handleInputChange('part_number_master', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Revision</label>
                        <input
                          type="text"
                          value={formData.revision || ''}
                          onChange={(e) => handleInputChange('revision', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Description</label>
                        <input
                          type="text"
                          value={formData.description || ''}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Material</label>
                        <input
                          type="text"
                          value={formData.material || ''}
                          onChange={(e) => handleInputChange('material', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <button
                          onClick={() => handleSave('general')}
                          className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Part Number Master</p>
                        <p className="text-sm text-slate-800">{part.part_number_master || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Revision</p>
                        <p className="text-sm text-slate-800">{part.revision || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Description</p>
                        <p className="text-sm text-slate-800">{part.description || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Material</p>
                        <p className="text-sm text-slate-800">{part.material || '-'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Production Specifications */}
              <div className="bg-white rounded-sm border border-slate-200">
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Production Specifications</h2>
                  <button
                    onClick={() => handleEditToggle('production')}
                    className="px-2.5 py-1.5 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                  >
                    {editMode.section === 'production' && editMode.enabled ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                <div className="p-4">
                  {editMode.section === 'production' && editMode.enabled ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Last Run Date</label>
                        <input
                          type="date"
                          value={formData.last_run_date ? new Date(formData.last_run_date).toISOString().split('T')[0] : ''}
                          onChange={(e) => handleInputChange('last_run_date', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Average Run Time (Days)</label>
                        <input
                          type="number"
                          value={formData.average_run_time || ''}
                          onChange={(e) => handleInputChange('average_run_time', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Setup Notes</label>
                        <textarea
                          value={formData.setup_notes || ''}
                          onChange={(e) => handleInputChange('setup_notes', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          rows="2"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Batch Size</label>
                        <input
                          type="number"
                          value={formData.batch_size || ''}
                          onChange={(e) => handleInputChange('batch_size', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          min="0"
                        />
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <button
                          onClick={() => handleSave('production')}
                          className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Last Run Date</p>
                        <p className="text-sm text-slate-800 tabular-nums">{part.last_run_date ? new Date(part.last_run_date).toLocaleDateString() : '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Average Run Time (Days)</p>
                        <p className="text-sm text-slate-800 tabular-nums">{part.average_run_time || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Setup Notes</p>
                        <p className="text-sm text-slate-800">{part.setup_notes || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Batch Size</p>
                        <p className="text-sm text-slate-800 tabular-nums">{part.batch_size || '-'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Inventory & Cost */}
              <div className="bg-white rounded-sm border border-slate-200">
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Inventory & Cost</h2>
                  <button
                    onClick={() => handleEditToggle('inventory')}
                    className="px-2.5 py-1.5 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                  >
                    {editMode.section === 'inventory' && editMode.enabled ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                <div className="p-4">
                  {editMode.section === 'inventory' && editMode.enabled ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Current Inventory</label>
                        <p className="px-2 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-sm tabular-nums">
                          {part.inventory_quantity || '0'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Unit Price</label>
                        <input
                          type="number"
                          value={formData.unit_price || ''}
                          onChange={(e) => handleInputChange('unit_price', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-slate-500 mb-1">Inventory Adjustment</label>
                        <input
                          type="number"
                          value={formData.inventory_adjustment || ''}
                          onChange={(e) => handleInputChange('inventory_adjustment', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                          placeholder="Positive to add, negative to subtract"
                        />
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <button
                          onClick={() => handleSave('inventory')}
                          className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Current Inventory</p>
                        <p className="text-sm text-slate-800 tabular-nums">{part.inventory_quantity || '0'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Unit Price</p>
                        <p className="text-sm text-slate-800 tabular-nums">${part.unit_price || '0.00'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Supplier & Meta Data */}
              <div className="bg-white rounded-sm border border-slate-200">
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Supplier & Meta Data</h2>
                  <button
                    onClick={() => handleEditToggle('meta')}
                    className="px-2.5 py-1.5 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                  >
                    {editMode.section === 'meta' && editMode.enabled ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                <div className="p-4">
                  {editMode.section === 'meta' && editMode.enabled ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Shop</label>
                        <select
                          value={formData.shop_id || ''}
                          onChange={(e) => handleInputChange('shop_id', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        >
                          <option value="">Select Shop</option>
                          {shops.map(shop => (
                            <option key={shop.id} value={shop.id}>{shop.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Machine</label>
                        <select
                          value={formData.machine_id || ''}
                          onChange={(e) => handleInputChange('machine_id', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        >
                          <option value="">Select Machine</option>
                          {machines.map(machine => (
                            <option key={machine.id} value={machine.id}>{machine.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Supplier</label>
                        <select
                          value={formData.supplier_id || ''}
                          onChange={(e) => handleInputChange('supplier_id', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        >
                          <option value="">Select Supplier</option>
                          {suppliers.map(supplier => (
                            <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Status</label>
                        <select
                          value={formData.status || ''}
                          onChange={(e) => handleInputChange('status', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        >
                          <option value="Active">Active</option>
                          <option value="Obsolete">Obsolete</option>
                          <option value="In Development">In Development</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-slate-500 mb-1">Production Notes</label>
                        <textarea
                          value={formData.production_notes || ''}
                          onChange={(e) => handleInputChange('production_notes', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          rows="2"
                        />
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <button
                          onClick={() => handleSave('meta')}
                          className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Shop</p>
                        <p className="text-sm text-slate-800">{part.shops?.name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Machine</p>
                        <p className="text-sm text-slate-800">{part.machines?.name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Supplier</p>
                        <p className="text-sm text-slate-800">{part.suppliers?.name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Created</p>
                        <p className="text-sm text-slate-800 tabular-nums">{new Date(part.created_at).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Updated</p>
                        <p className="text-sm text-slate-800 tabular-nums">{new Date(part.updated_at).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Production Notes</p>
                        <p className="text-sm text-slate-800">{part.production_notes || '-'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Tab.Panel>

            {/* Production Logs Panel */}
            <Tab.Panel>
              <div className="bg-white rounded-sm border border-slate-200">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Production Logs</h2>
                </div>
                {productionLogs.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Job ID</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Quantity</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Shop</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Machine</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {productionLogs.map((log) => (
                        <tr key={log.log_id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-3 py-2 text-sm text-slate-600">{log.job_id}</td>
                          <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{log.quantity_produced}</td>
                          <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{new Date(log.production_date).toLocaleDateString()}</td>
                          <td className="px-3 py-2 text-sm text-slate-600">{log.shops?.name || '-'}</td>
                          <td className="px-3 py-2 text-sm text-slate-600">{log.machines?.name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="px-4 py-6 text-sm text-slate-500 text-center">No production logs recorded for this part.</p>
                )}
              </div>
            </Tab.Panel>

            {/* Files Panel */}
            <Tab.Panel>
              <div className="bg-white rounded-sm border border-slate-200">
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Files</h2>
                  <button
                    onClick={() => setIsUploadModalOpen(true)}
                    className="px-2.5 py-1.5 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                  >
                    Upload File
                  </button>
                </div>
                {partFiles.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">File Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Uploaded</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {partFiles.map((file) => (
                        <tr key={file.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-3 py-2 text-sm font-medium text-slate-800">{file.file_name}</td>
                          <td className="px-3 py-2 text-sm text-slate-600">{file.file_type}</td>
                          <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{new Date(file.uploaded_at).toLocaleDateString()}</td>
                          <td className="px-3 py-2 text-sm">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleFilePreview(file.file_path)}
                                className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                              >
                                Preview
                              </button>
                              <button
                                onClick={() => handleFileDownload(file.file_path)}
                                className="text-xs text-blue-600 hover:text-blue-700 transition-colors"
                              >
                                Download
                              </button>
                              <button
                                onClick={() => handleFileDelete(file.id, file.file_path)}
                                className="text-xs text-red-500 hover:text-red-700 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="px-4 py-6 text-sm text-slate-500 text-center">No files uploaded for this part.</p>
                )}
              </div>
            </Tab.Panel>

            {/* Quote Panel */}
            <Tab.Panel>
              <div className="bg-white rounded-sm border border-slate-200 p-4">
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Generate Quote</h2>
                <ManualQuote
                  part={{
                    partNumber: part.part_number_master,
                    revision: part.revision,
                    description: part.description,
                  }}
                  onSave={handleQuoteSave}
                />
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>

      {/* Upload File Modal */}
      <Transition appear show={isUploadModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsUploadModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                    <Dialog.Title className="text-sm font-semibold text-slate-800">
                      Upload New File
                    </Dialog.Title>
                    <button onClick={() => setIsUploadModalOpen(false)} className="text-slate-400 hover:text-slate-600">×</button>
                  </div>
                  <div className="p-4 space-y-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">File</label>
                      <input
                        type="file"
                        onChange={(e) => setFileToUpload(e.target.files[0])}
                        className="w-full text-sm text-slate-600 file:mr-4 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">File Type</label>
                      <select
                        value={fileType}
                        onChange={(e) => setFileType(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                      >
                        <option value="">Select File Type</option>
                        <option value="Drawing">Drawing</option>
                        <option value="PO">PO</option>
                        <option value="Material Invoice">Material Invoice</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
                    <button
                      onClick={() => {
                        setFileToUpload(null);
                        setFileType('');
                        setIsUploadModalOpen(false);
                      }}
                      className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleFileUpload}
                      className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                    >
                      Upload
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Preview File Modal */}
      <Transition appear show={isPreviewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsPreviewModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-[90vw] h-[90vh] transform rounded-sm bg-white shadow-xl transition-all">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                    <Dialog.Title className="text-sm font-semibold text-slate-800">
                      File Preview
                    </Dialog.Title>
                    <button onClick={() => setIsPreviewModalOpen(false)} className="text-slate-400 hover:text-slate-600">×</button>
                  </div>
                  <div className="h-[calc(100%-7rem)] p-4">
                    {previewFile ? (
                      <iframe
                        src={previewFile}
                        className="w-full h-full border border-slate-200 rounded-sm"
                        title="File Preview"
                      />
                    ) : (
                      <p className="text-sm text-slate-500">Unable to preview this file type.</p>
                    )}
                  </div>
                  <div className="flex justify-end px-4 py-3 border-t border-slate-100 bg-slate-50">
                    <button
                      onClick={() => setIsPreviewModalOpen(false)}
                      className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Confirmation Modal */}
      <Transition appear show={confirmModal.isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                  <div className={`flex items-center justify-between px-4 py-3 border-b ${
                    confirmModal.variant === 'destructive' ? 'bg-red-50 border-red-100' :
                    confirmModal.variant === 'danger' ? 'bg-red-50 border-red-100' :
                    'bg-slate-50 border-slate-200'
                  }`}>
                    <Dialog.Title className={`text-sm font-semibold ${
                      confirmModal.variant === 'destructive' || confirmModal.variant === 'danger' ? 'text-red-800' :
                      'text-slate-800'
                    }`}>
                      {confirmModal.title}
                    </Dialog.Title>
                    <button
                      onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      ×
                    </button>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-slate-600">{confirmModal.message}</p>
                  </div>
                  <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
                    <button
                      onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                      className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmModal.onConfirm}
                      className={`px-3 py-1.5 text-sm text-white rounded-sm transition-colors ${
                        confirmModal.variant === 'destructive' || confirmModal.variant === 'danger'
                          ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-slate-700 hover:bg-slate-800'
                      }`}
                    >
                      {confirmModal.variant === 'destructive' || confirmModal.variant === 'danger' ? 'Delete' : 'Confirm'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default Part;
